import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { DataService } from './data.service';
import { PostFilterStore } from './post-filter.store';

/**
 * Recovers from a filter that names a channel the editor deleted mid-session.
 *
 * The user has the page open, the editor removes channel X, the user clicks
 * the X chip and the server answers 404 — the truthful status, since the
 * addressed resource is gone. That status is also a precise trigger: the chip
 * row is stale for exactly the same reason, so the instance payload is
 * refetched and the filter falls back to `All`.
 *
 * Shared by every post widget, since each one issues its own request and would
 * otherwise repeat the recovery. Any other error is passed through untouched:
 * only a missing channel is something the client can put right by itself.
 */
@Injectable({ providedIn: 'root' })
export class ChannelResetService {
  private readonly dataService = inject(DataService);
  private readonly filterStore = inject(PostFilterStore);

  /**
   * Always completes with `null`, so the caller can treat the outcome as
   * "no result this time" and render its error state.
   */
  handle(error: unknown): Observable<null> {
    const isMissingChannel =
      error instanceof HttpErrorResponse &&
      error.status === 404 &&
      this.filterStore.channel() !== null;

    if (!isMissingChannel) return of(null);

    // Resetting the filter re-runs every widget's pipeline, which is how the
    // feed refills — no explicit reload is needed here.
    this.filterStore.channel.set(null);

    return this.dataService.refreshInstance().pipe(
      map((): null => null),
      catchError(() => of(null)),
    );
  }
}
